import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
	CreateAccount,
	DeleteAccount,
	GetAccount,
	GetAccounts,
	UpdateAccount,
	GetSite as AdminGetSite,
	GetSites as AdminGetSites,
	CreateSite as AdminCreateSite,
	UpdateSite as AdminUpdateSite,
	DeleteSite as AdminDeleteSite,
	GetSiteDimensions as AdminGetSiteDimensions,
	GetSiteUsers as AdminGetSiteUsers,
	InviteSiteUser as AdminInviteSiteUser,
	UpdateSiteUser as AdminUpdateSiteUser,
	GetSiteUser as AdminGetSiteUser,
	GetUserInformationByEmail,
	RemoveSiteUser as AdminRemoveSiteUser,
	ResendActivationCode,
} from '../SharedTypes/API/Admin';
import {
	GetElementDimensions,
	GetJointDimensions,
	GetSectionResults,
	GetSiteGeometry,
} from '../SharedTypes/API/Explorer';
import { GetSites, GetSiteSections } from '../SharedTypes/API/Site';
import {
	GetElementMetrics,
	GetLatestPointTime,
	GetSiteMetrics,
	GetQuickReference,
	GetMetric,
} from '../SharedTypes/API/Dashboard';
import {
	CreatePassword,
	GetUser,
	LoginUser,
	LogoutUser,
	GetIsUserEmailVerified,
	ForgotPassword,
	ConfirmForgotPasswordCode,
} from '../SharedTypes/API/Auth';
import { makeQuery } from './utils/makeQuery';
import {
	GetTrends,
	GetTrendsQuickReference,
	GetTrendTemplates,
} from '../SharedTypes/API/Trends';

// Define a service using a base URL and expected endpoints
export const api = createApi({
	reducerPath: 'api',
	baseQuery: fetchBaseQuery(),
	tagTypes: [
		'accounts',
		'sites',
		'AdminSite',
		'AdminSiteDimensions',
		'AdminSiteUsers',
	],
	endpoints: (builder) => ({
		getSiteGeometry: builder.query<
			GetSiteGeometry['return'],
			GetSiteGeometry['arguments']
		>({
			query: makeQuery<GetSiteGeometry>('/api/explorer/getSiteGeometry'),
		}),
		getSectionResults: builder.query<
			GetSectionResults['return'],
			GetSectionResults['arguments']
		>({
			query: makeQuery<GetSectionResults>('/api/explorer/getSectionResults'),
		}),
		getElementDimensions: builder.query<
			GetElementDimensions['return'],
			GetElementDimensions['arguments']
		>({
			query: makeQuery<GetElementDimensions>(
				'/api/explorer/getElementDimensions'
			),
		}),
		getJointDimensions: builder.query<
			GetJointDimensions['return'],
			GetJointDimensions['arguments']
		>({
			query: makeQuery<GetJointDimensions>('/api/explorer/getJointDimensions'),
		}),
		getSites: builder.query<GetSites['return'], GetSites['arguments']>({
			query: makeQuery<GetSites>('/api/site/getSites'),
			providesTags: ['sites'],
		}),
		getSiteSections: builder.query<
			GetSiteSections['return'],
			GetSiteSections['arguments']
		>({
			query: makeQuery<GetSiteSections>('/api/site/getSiteSections'),
		}),
		getUser: builder.query<GetUser['return'], GetUser['arguments']>({
			query: makeQuery<GetUser>('/api/auth/getUser'),
		}),
		GetIsUserEmailVerified: builder.query<
			GetIsUserEmailVerified['return'],
			GetIsUserEmailVerified['arguments']
		>({
			query: makeQuery<GetIsUserEmailVerified>(
				'/api/auth/getIsUserEmailVerified'
			),
		}),
		loginUser: builder.mutation<LoginUser['return'], LoginUser['arguments']>({
			query: makeQuery<LoginUser>('/api/auth/loginUser'),
		}),
		logoutUser: builder.mutation<LogoutUser['return'], LogoutUser['arguments']>(
			{
				query: makeQuery<LogoutUser>('/api/auth/logoutUser'),
			}
		),
		createPassword: builder.mutation<
			CreatePassword['return'],
			CreatePassword['arguments']
		>({
			query: makeQuery<CreatePassword>('/api/auth/createPassword'),
		}),
		forgotPassword: builder.mutation<
			ForgotPassword['return'],
			ForgotPassword['arguments']
		>({
			query: makeQuery<ForgotPassword>('/api/auth/forgotPassword'),
		}),
		confirmForgotPasswordCode: builder.mutation<
			ConfirmForgotPasswordCode['return'],
			ConfirmForgotPasswordCode['arguments']
		>({
			query: makeQuery<ConfirmForgotPasswordCode>(
				'/api/auth/forgotPassword/confirmcode'
			),
		}),

		/** Dashboards */
		getQuickReference: builder.query<
			GetQuickReference['return'],
			GetQuickReference['arguments']
		>({
			query: makeQuery<GetQuickReference>('/api/dashboard/getQuickReference'),
		}),
		getSiteMetrics: builder.query<
			GetSiteMetrics['return'],
			GetSiteMetrics['arguments']
		>({
			query: makeQuery<GetSiteMetrics>('/api/dashboard/getSiteMetrics'),
		}),
		getElementMetrics: builder.query<
			GetElementMetrics['return'],
			GetElementMetrics['arguments']
		>({
			query: makeQuery<GetElementMetrics>('/api/dashboard/getElementMetrics'),
		}),
		getLatestPointTime: builder.query<
			GetLatestPointTime['return'],
			GetLatestPointTime['arguments']
		>({
			query: makeQuery<GetLatestPointTime>('/api/dashboard/getLatestPointTime'),
		}),
		getMetric: builder.query<GetMetric['return'], GetMetric['arguments']>({
			query: makeQuery<GetMetric>('/api/dashboard/getMetric'),
		}),
		getTrendTemplates: builder.query<
			GetTrendTemplates['return'],
			GetTrendTemplates['arguments']
		>({
			query: makeQuery<GetTrendTemplates>('/api/trends/getTrendTemplates'),
		}),
		getTrends: builder.query<GetTrends['return'], GetTrends['arguments']>({
			query: makeQuery<GetTrends>('/api/trends/getTrend'),
		}),
		getTrendsQuickReference: builder.query<
			GetTrendsQuickReference['return'],
			GetTrendsQuickReference['arguments']
		>({
			query: makeQuery<GetTrendsQuickReference>(
				'/api/trends/getTrendsQuickReference'
			),
		}),

		// ############################################################
		// 											ADMIN BLOCK
		// ############################################################

		// Accounts
		adminGetAccounts: builder.query<
			GetAccounts['return'],
			GetAccounts['arguments']
		>({
			query: makeQuery<GetAccounts>('/api/admin/account/getAccounts'),
			providesTags: ['accounts'],
		}),
		adminGetAccount: builder.query<
			GetAccount['return'],
			GetAccount['arguments']
		>({
			query: makeQuery<GetAccount>('/api/admin/account/getAccount'),
			providesTags: ['accounts'],
		}),
		adminUpdateAccount: builder.mutation<
			UpdateAccount['return'],
			UpdateAccount['arguments']
		>({
			query: makeQuery<UpdateAccount>('/api/admin/account/updateAccount'),
			invalidatesTags: ['accounts'],
		}),
		adminCreateAccount: builder.mutation<
			CreateAccount['return'],
			CreateAccount['arguments']
		>({
			query: makeQuery<CreateAccount>('/api/admin/account/createAccount'),
			invalidatesTags: ['accounts'],
		}),
		adminDeleteAccount: builder.mutation<
			DeleteAccount['return'],
			DeleteAccount['arguments']
		>({
			query: makeQuery<DeleteAccount>('/api/admin/account/deleteAccount'),
			invalidatesTags: ['accounts'],
		}),
		adminResendActivationCode: builder.mutation<
			ResendActivationCode['return'],
			ResendActivationCode['arguments']
		>({
			query: makeQuery<ResendActivationCode>(
				'/api/admin/account/resendActivationCode'
			),
		}),

		// Sites
		adminGetSite: builder.query<
			AdminGetSite['return'],
			AdminGetSite['arguments']
		>({
			query: makeQuery<AdminGetSite>('/api/admin/site/getSite'),
			providesTags: (_result, _error, { siteId }) => [
				{ type: 'AdminSite', id: siteId },
			],
		}),
		adminGetSites: builder.query<
			AdminGetSites['return'],
			AdminGetSites['arguments']
		>({
			query: makeQuery<AdminGetSites>('/api/admin/site/getSites'),
			providesTags: (result) =>
				result
					? [
							...result.sites.map(({ id }) => ({
								type: 'AdminSite' as const,
								id,
							})),
							{ type: 'AdminSite', id: 'LIST' },
					  ]
					: [{ type: 'AdminSite', id: 'LIST' }],
		}),
		adminCreateSite: builder.mutation<
			AdminCreateSite['return'],
			AdminCreateSite['arguments']
		>({
			query: makeQuery<AdminCreateSite>('/api/admin/site/createSite'),
			// Invalidates everything, since we added something
			invalidatesTags: [{ type: 'AdminSite', id: 'LIST' }],
		}),
		adminUpdateSite: builder.mutation<
			AdminUpdateSite['return'],
			AdminUpdateSite['arguments']
		>({
			query: makeQuery<AdminUpdateSite>('/api/admin/site/updateSite'),
			// Only invalidates the one, it seems?
			invalidatesTags: (_result, _error, arg) => [
				{ type: 'AdminSite', id: arg.id },
			],
		}),
		adminDeleteSite: builder.mutation<
			AdminDeleteSite['return'],
			AdminDeleteSite['arguments']
		>({
			query: makeQuery<AdminDeleteSite>('/api/admin/site/deleteSite'),
			invalidatesTags: (_result, _error, arg) => [
				{ type: 'AdminSite', id: arg.siteId },
				{ type: 'AdminSite', id: 'LIST' },
			],
		}),

		// Site Dimensions
		adminGetSiteDimensions: builder.query<
			AdminGetSiteDimensions['return'],
			AdminGetSiteDimensions['arguments']
		>({
			query: makeQuery<AdminGetSiteDimensions>(
				'/api/admin/sitedimension/getSiteDimensions'
			),
			providesTags: ['AdminSiteDimensions'],
		}),

		// Site Users
		adminGetSiteUsers: builder.query<
			AdminGetSiteUsers['return'],
			AdminGetSiteUsers['arguments']
		>({
			query: makeQuery<AdminGetSiteUsers>('/api/admin/siteuser/getSiteUsers'),
			providesTags: ['AdminSiteUsers'],
		}),

		adminGetSiteUser: builder.query<
			AdminGetSiteUser['return'],
			AdminGetSiteUser['arguments']
		>({
			query: makeQuery<AdminGetSiteUser>('/api/admin/siteuser/getSiteUser'),
			providesTags: ['AdminSiteUsers'],
		}),

		adminInviteSiteUser: builder.mutation<
			AdminInviteSiteUser['return'],
			AdminInviteSiteUser['arguments']
		>({
			query: makeQuery<AdminInviteSiteUser>(
				'/api/admin/siteuser/inviteSiteUser'
			),
			invalidatesTags: ['AdminSiteUsers'],
		}),

		adminUpdateSiteUser: builder.mutation<
			AdminUpdateSiteUser['return'],
			AdminUpdateSiteUser['arguments']
		>({
			query: makeQuery<AdminUpdateSiteUser>(
				'/api/admin/siteuser/updateSiteUser'
			),
			invalidatesTags: ['AdminSiteUsers'],
		}),

		adminGetUserInformationByEmail: builder.query<
			GetUserInformationByEmail['return'],
			GetUserInformationByEmail['arguments']
		>({
			query: makeQuery<GetUserInformationByEmail>(
				'/api/admin/user/getUserInformationByEmail'
			),
		}),
		adminRemoveSiteUser: builder.mutation<
			AdminRemoveSiteUser['return'],
			AdminRemoveSiteUser['arguments']
		>({
			query: makeQuery<AdminRemoveSiteUser>(
				'/api/admin/siteuser/removeSiteUser'
			),
			invalidatesTags: ['AdminSiteUsers'],
		}),
	}),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
	useGetElementDimensionsQuery,
	useGetJointDimensionsQuery,
	useGetSectionResultsQuery,
	useGetSitesQuery,
	useGetUserQuery,
	useLoginUserMutation,
	useLogoutUserMutation,
	useCreatePasswordMutation,
	useGetIsUserEmailVerifiedQuery,
	useGetQuickReferenceQuery,
	useAdminGetAccountsQuery,
	useAdminGetAccountQuery,
	useLazyAdminGetAccountQuery,
	useAdminUpdateAccountMutation,
	useAdminCreateAccountMutation,
	useAdminCreateSiteMutation,
	useAdminDeleteAccountMutation,
	useAdminDeleteSiteMutation,
	useAdminGetSiteDimensionsQuery,
	useAdminGetSiteQuery,
	useAdminGetSitesQuery,
	useAdminGetSiteUserQuery,
	useAdminGetSiteUsersQuery,
	useAdminInviteSiteUserMutation,
	useAdminRemoveSiteUserMutation,
	useAdminUpdateSiteMutation,
	useAdminUpdateSiteUserMutation,
	useGetElementMetricsQuery,
	useGetSiteGeometryQuery,
	useGetSiteSectionsQuery,
	useLazyGetSiteSectionsQuery,
	useGetSiteMetricsQuery,
	useLazyAdminGetSiteQuery,
	useLazyAdminGetUserInformationByEmailQuery,
	useGetTrendTemplatesQuery,
	useGetTrendsQuery,
	useGetTrendsQuickReferenceQuery,
	useForgotPasswordMutation,
	useConfirmForgotPasswordCodeMutation,
	useAdminResendActivationCodeMutation,
	useGetLatestPointTimeQuery,
	useGetMetricQuery,
} = api;
